import "./ListBox.scss";

import { Listbox, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Fragment, useRef } from "react";

import { ChevronDown } from "@/assets/icons/icons";
import {
  LISTBOX_OPTIONS_MAX_HEIGHT,
  useOpenUpward,
} from "@/hooks/useOpenUpward";

interface ListBoxContentProps<OptionType> {
  open: boolean;
  className?: string;
  label?: string;
  selected: OptionType | undefined;
  dataTestId?: string;
  displayValue: (value: OptionType) => string;
  availableValues: OptionType[];
  placeholder: string | OptionType;
  renderOption?: (
    option: OptionType,
    selected: boolean,
    active: boolean,
  ) => JSX.Element;
  disabled?: boolean;
}

// Split out from ListBox so useOpenUpward (a hook) is called from a proper
// component, not from Headless UI's render-prop callback directly.
const ListBoxContent = <OptionType,>({
  open,
  className,
  label,
  selected,
  dataTestId,
  displayValue,
  availableValues,
  placeholder,
  renderOption,
  disabled,
}: ListBoxContentProps<OptionType>) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const openUpward = useOpenUpward(
    wrapperRef,
    open,
    LISTBOX_OPTIONS_MAX_HEIGHT,
  );

  return (
    <div
      ref={wrapperRef}
      className={clsx(
        "listbox-container",
        openUpward && "opens-upward",
        className,
      )}
    >
      <div className="relative">
        <Listbox.Button
          data-testid={dataTestId}
          className={clsx("listbox-button", disabled && "opacity-25")}
        >
          {label && <span className="listbox-button__label">{label}</span>}
          <span className="listbox-button__value block truncate">
            {selected
              ? displayValue(selected)
              : typeof placeholder === "string"
                ? placeholder
                : displayValue(placeholder)}
          </span>
        </Listbox.Button>
        <div className={clsx("icon-wrapper", disabled && "opacity-25")}>
          <ChevronDown className="icon" aria-hidden="true" />
        </div>
      </div>

      <Transition
        show={open}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Listbox.Options className="listbox-options">
          {availableValues?.map((column, index) => (
            <Listbox.Option
              key={index}
              className={({ active }) => `option ${active ? "active" : ""}`}
              value={column}
            >
              {({ selected, active }) => (
                <>
                  <span className={`text ${selected ? "selected" : ""}`}>
                    {renderOption && renderOption(column, selected, active)}
                    {displayValue(column)}
                  </span>
                  {/* Selected option is marked by a vertical bar to the
                      left of the label (positioned off the option's own
                      edge, before the label's padding — not a checkmark,
                      and not overlapping the text). */}
                  {selected && (
                    <span className="selected-indicator" aria-hidden="true" />
                  )}
                </>
              )}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </Transition>
    </div>
  );
};

/** ListBox is a dropdown of options. It's generic, so it can be easily typed.
 *
 * If your selected option does not appear selected, and is complex type,
 * supply a compare function in the by propery.*/
export const ListBox = <OptionType,>({
  className,
  label,
  selected,
  setSelected,
  dataTestId,
  displayValue,
  availableValues,
  placeholder,
  by,
  renderOption,
  disabled,
}: {
  className?: string;
  /** Small caption ("Sprache") shown above the selected value. */
  label?: string;
  selected: OptionType | undefined;
  setSelected: (value: OptionType) => void;
  dataTestId?: string;
  displayValue: (value: OptionType) => string;
  availableValues: OptionType[];
  placeholder: string | OptionType;
  by?:
    | (keyof OptionType & string)
    | ((a: OptionType, z: OptionType) => boolean)
    | undefined;
  renderOption?: (
    option: OptionType,
    selected: boolean,
    active: boolean,
  ) => JSX.Element;
  disabled?: boolean;
}) => {
  return (
    <Listbox
      {...(by && { by })}
      value={selected}
      onChange={(value: OptionType) => {
        setSelected(value);
      }}
      disabled={disabled}
    >
      {({ open }) => (
        <ListBoxContent<OptionType>
          open={open}
          className={className}
          label={label}
          selected={selected}
          dataTestId={dataTestId}
          displayValue={displayValue}
          availableValues={availableValues}
          placeholder={placeholder}
          renderOption={renderOption}
          disabled={disabled}
        />
      )}
    </Listbox>
  );
};
